/* One hint at a time, sitewide. Every live HoverWord registers as a host; a
   single loop picks a random word that is on screen and not mid-play,
   wakes its hint, then rests before the next pick — a glance of delight,
   never a chorus. */

export interface HintHost {
  visible: () => boolean;
  busy: () => boolean;
  /** Show the hint; returns how long it will stay up (ms). */
  wake: () => number;
  /** Bow the hint out (short exit, idempotent). */
  hide: () => void;
}

const hosts = new Set<HintHost>();
let timer = 0;
let last: HintHost | null = null;
let current: HintHost | null = null;
let playing = 0;

/* The stage is exclusive between words too: claiming it stops whichever
   other word is mid-performance via its own clean fade-out path. */
let stageStop: (() => void) | null = null;

export function claimStage(stop: () => void) {
  if (stageStop && stageStop !== stop) stageStop();
  stageStop = stop;
}

export function releaseStage(stop: () => void) {
  if (stageStop === stop) stageStop = null;
}

/* While ANY word performs its overlay, the stage is its alone: the live
   hint is cut and no new one is picked until the performance ends. */
export function overlayStarted() {
  playing++;
  current?.hide();
  current = null;
}

export function overlayEnded() {
  playing = Math.max(0, playing - 1);
}

function tick() {
  if (playing > 0) {
    timer = window.setTimeout(tick, 1600);
    return;
  }
  const ready = [...hosts].filter((h) => h.visible() && !h.busy());
  const pool = ready.length > 1 ? ready.filter((h) => h !== last) : ready;
  if (!pool.length) {
    timer = window.setTimeout(tick, 1600);
    return;
  }
  const pick = pool[Math.floor(Math.random() * pool.length)];
  last = pick;
  current = pick;
  const shownFor = pick.wake();
  timer = window.setTimeout(tick, shownFor + 1400 + Math.random() * 1800);
}

export function registerHintHost(host: HintHost): () => void {
  hosts.add(host);
  if (hosts.size === 1) {
    timer = window.setTimeout(tick, 2400 + Math.random() * 1200);
  }
  return () => {
    hosts.delete(host);
    if (last === host) last = null;
    if (current === host) current = null;
    if (!hosts.size) window.clearTimeout(timer);
  };
}
